import React, { useState } from 'react'
import useScrollbarSize from 'react-scrollbar-size'
import { Grid, Stack, useMediaQuery, useTheme } from '@mui/material'
import { makeStyles } from '@mui/styles'
import classNames from 'classnames'

import { ClassesType, PrimitiveTableColumn, TableFilter } from '../../../types'
import {
  getColumnKey,
  getColumnStyles,
} from '../../../utils/primitiveTableUtils'
import AppliedFilters, { SortingType } from './filters/AppliedFilters'
import FilterLabel from './filters/FilterLabel'

const useStyles = makeStyles(
  theme => ({
    tableHeader: {
      padding: theme.spacing(1, 2),
      // The additional scrollbarWidth padding points that fits content scrollbar size
      paddingRight: ({ hasScrollSpace, scrollbarWidth }: UseStylesProps) =>
        parseInt(theme.spacing(2), 10) + (hasScrollSpace ? scrollbarWidth : 0),
      minHeight: 50,
      border: theme.constants.tableBorder,
      borderTopWidth: 0,
      borderBottomWidth: 0,
      borderColor: 'transparent',
    },
    expandedColumn: {
      transition: theme.transitions.create('max-width', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.enteringScreen,
      }),
    },
    collapsedColumn: {
      transition: theme.transitions.create('max-width', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
      }),
    },
    expandedMainColumn: {
      minWidth: 120,
      transition: theme.transitions.create('max-width', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.enteringScreen / 2,
        delay: 100,
      }),
    },
    fillParent: {
      width: '100%',
      height: '100%',
    },
    controlButtonsHeader: {
      minWidth: 75,
    },
  }),
  { name: 'PrimitiveTableHeader' },
)

export interface PrimitiveTableHeaderProps {
  readonly classes?: ClassesType<typeof useStyles>
  readonly expandable: boolean
  readonly filters?: Record<string, TableFilter>
  readonly hasScrollSpace?: boolean
  readonly hasSelectedItem: boolean
  readonly mainColumnWidth: number
  readonly mainColumns: PrimitiveTableColumn[]
  readonly onApplyFilter: (filter: string, value: TableFilter) => void
  readonly onApplySorting?: (sort: string, value: boolean) => void
  readonly onClearFilters: () => void
  readonly onClearSorting: (sortName: string) => void
  readonly otherColumns: PrimitiveTableColumn[]
  readonly showControlButtons: boolean
  readonly sortings?: Record<string, boolean>
  readonly useNilAsEmpty?: boolean
}

interface UseStylesProps {
  classes?: ClassesType<typeof useStyles>
  hasScrollSpace: boolean
  mainColumnWidth: number
  scrollbarWidth: number
}

const PrimitiveTableHeader = ({
  filters: filtersProp,
  sortings: sortingsProp,
  expandable,
  mainColumnWidth,
  mainColumns,
  otherColumns,
  showControlButtons,
  hasSelectedItem,
  useNilAsEmpty = false,
  onApplyFilter,
  onApplySorting,
  onClearFilters,
  onClearSorting,
  classes: classesProp,
  hasScrollSpace = false,
}: PrimitiveTableHeaderProps) => {
  const filters = filtersProp || {}
  const sortings = sortingsProp || {}
  const { width: scrollbarWidth } = useScrollbarSize()

  const useStylesProps: UseStylesProps = {
    mainColumnWidth,
    classes: classesProp,
    hasScrollSpace,
    scrollbarWidth,
  }
  const classes = useStyles(useStylesProps)
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))

  const [openFilter, setOpenFilter] = useState<string>()

  const onFilterClose = () => {
    setOpenFilter(undefined)
  }

  const onFilterClick = (filter?: string) => {
    setOpenFilter(openFilter === filter ? undefined : filter)
  }

  if (isMobile) {
    return null
  }

  const columnHeaders = (
    <>
      {otherColumns.map(
        (
          {
            label,
            sublabel,
            width,
            filter,
            sorting,
            FilterComponent,
            className,
            hidden,
            wrap,
            SortingIcon,
          },
          index,
        ) =>
          !hidden ? (
            <Grid
              item
              key={getColumnKey({ label, sublabel }, index)}
              style={{ ...getColumnStyles(width) }}
            >
              {!hasSelectedItem && (
                <FilterLabel
                  FilterComponent={FilterComponent}
                  SortingIcon={SortingIcon}
                  className={className}
                  filter={filter}
                  filterValue={filter ? filters[filter] : undefined}
                  label={label}
                  open={openFilter === filter}
                  sorting={sorting}
                  sortingValue={sorting ? sortings[sorting] : undefined}
                  sublabel={sublabel}
                  wrap={wrap}
                  onApplyFilter={onApplyFilter}
                  onApplySorting={onApplySorting}
                  onFilterClick={onFilterClick}
                  onFilterClose={onFilterClose}
                />
              )}
            </Grid>
          ) : null,
      )}
      {showControlButtons && (
        <Grid item className={classes.controlButtonsHeader} md={1} />
      )}
    </>
  )

  const onRemoveSorting = onClearSorting
    ? (sort: string) => onClearSorting(sort)
    : () => {}

  // sortingObjects will first check 'otherColumns' and fallback to 'mainColumns'
  const sortingObjects = Object.keys(sortings).map(sortingName => {
    // Searching in 'otherColumns' first
    let humanReadableName = otherColumns.find(
      item => item.sorting === sortingName,
    )?.label
    // If not found in 'otherColumns', check 'mainColumns'
    if (!humanReadableName) {
      humanReadableName = mainColumns.find(
        item => item.sorting === sortingName,
      )?.label
    }
    return {
      sortingName,
      humanReadableName,
    } as SortingType
  })

  return (
    <>
      <AppliedFilters
        filters={filters}
        sortings={sortingObjects}
        useNilAsEmpty={useNilAsEmpty}
        onClearFilters={onClearFilters}
        onRemoveFilter={filter => onApplyFilter(filter, { value: undefined })}
        onRemoveSorting={onRemoveSorting}
      />
      <Grid container alignItems="center" className={classes.tableHeader}>
        <Stack
          alignItems="center"
          className={classes.fillParent}
          direction="row"
        >
          <Grid
            container
            item
            alignItems="center"
            className={classNames({
              [classes.expandedMainColumn]: !hasSelectedItem,
            })}
            style={{ ...getColumnStyles(mainColumnWidth) }}
            wrap="nowrap"
          >
            {mainColumns.map((column, index) => (
              <Grid
                item
                key={getColumnKey(column, index)}
                style={{
                  ...getColumnStyles(
                    (column.width * 12) /
                      (hasSelectedItem ? 1 : mainColumnWidth),
                  ),
                }}
              >
                <FilterLabel
                  FilterComponent={column.FilterComponent}
                  SortingIcon={column.SortingIcon}
                  filter={column.filter}
                  filterValue={
                    column.filter ? filters[column.filter] : undefined
                  }
                  label={column.label}
                  open={openFilter === column.filter}
                  sorting={column.sorting}
                  sortingValue={
                    column.sorting ? sortings[column.sorting] : undefined
                  }
                  sublabel={column.sublabel}
                  wrap={column.wrap}
                  onApplyFilter={onApplyFilter}
                  onApplySorting={onApplySorting}
                  onFilterClick={onFilterClick}
                  onFilterClose={onFilterClose}
                />
              </Grid>
            ))}
          </Grid>
          {expandable ? (
            <Grid
              container
              item
              alignItems="center"
              className={classNames({
                [classes.collapsedColumn]: hasSelectedItem,
                [classes.expandedColumn]: !hasSelectedItem,
              })}
              style={getColumnStyles(
                hasSelectedItem ? 0 : 12 - mainColumnWidth,
              )}
              wrap="nowrap"
            >
              {columnHeaders}
            </Grid>
          ) : (
            columnHeaders
          )}
        </Stack>
      </Grid>
    </>
  )
}

export default PrimitiveTableHeader
